<script lang="ts" setup generic="T">
import type { UseQueryOptions } from '@tanstack/vue-query';
import type { PropType } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import EmptyState from '@/components/EmptyState.vue';

export type AgListTooltipItem = {
    primary: string;
    secondary?: string | null;
};

export type AgListTooltipValue<T> = {
    queryOptions: UseQueryOptions<T, Error, AgListTooltipItem[], T>;
};

const props = defineProps({
    params: {
        type: Object as PropType<{ value: AgListTooltipValue<T> }>,
        required: true,
    },
});

const { data, isFetching, error } = useQuery(props.params.value.queryOptions);

</script>

<template>
    <SStack vertical class="tooltip-wrapper sy-elevation-default bg-white p-3">
        <LoadingSpinner
            v-if="isFetching === true"
            variant="primary"
            size="small"
            :loading-message="$t('system.loading')"
        />
        <EmptyState v-else-if="error != null" :text="$t('components.agListTooltip.error')" />
        <EmptyState v-else-if="data == null || data.length === 0" :text="$t('components.agListTooltip.empty')" />
        <SStack v-else vertical gap="5">
            <SStack
                v-for="(item, index) in data"
                :key="index"
                vertical
            >
                <SText is="p">{{ item.primary }}</SText>
                <SText is="p" variant="secondary">{{ item.secondary ?? '-' }}</SText>
            </SStack>
        </SStack>
    </SStack>
</template>

<style scoped>
.tooltip-wrapper {
    max-height: 300px;
    min-width: 250px;
    overflow-y: auto;
}
</style>
